import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { TabMenuModule } from 'primeng/tabmenu';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { NewLinkedinTemplateComponent } from 'src/app/components/linkedin-outreach/new-linkedin-template/new-linkedin-template.component';
import { NewLinkedinSequenceComponent } from 'src/app/components/linkedin-outreach/new-linkedin-sequence/new-linkedin-sequence.component';
import { LeadsList, LinkedInSequence, LinkedInTemplate, OutreachSequence, Template, templateTypes } from 'src/app/models/linkedin.model';
import { ToastModule } from 'primeng/toast';
import { IntegrationsService } from 'src/app/services/integrations.service';
import { OutreachService } from 'src/app/services/outreach.service';
import { AuthService } from 'src/app/services/auth.service';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, forkJoin, of, Subject, takeUntil, tap } from 'rxjs';
import { Integration } from 'src/app/models/integration.model';
import { LinkedInService } from 'src/app/services/linkedin.service';
import { EmailSequence, NewSequenceResponse } from 'src/app/models/email-sequence.model';
import { ProspectImportDialogComponent } from 'src/app/components/prospect-import-dialog/prospect-import-dialog.component';
import { ProspectService } from 'src/app/services/prospect.service';
import { DataView } from 'primeng/dataview';
import { Contact } from 'src/app/models/message.model';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { CompaniesService } from 'src/app/services/companies.service';
import { SelectableCompany } from 'src/app/models/company.model';
import { CompanyHeaderComponent } from 'src/app/components/company-header/company-header.component';
import { RequiresCapabilityDirective } from 'src/app/directives/requires-capability.directive';

@Component({
  selector: 'app-linkedin-main',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CardModule,
    RippleModule,
    TableModule,
    TabViewModule,
    TagModule,
    ToastModule,
    DataView,
    TooltipModule,
    TabMenuModule,
    ConfirmDialog,
    NewLinkedinTemplateComponent,
    NewLinkedinSequenceComponent,
    ProspectImportDialogComponent,
    CompanyHeaderComponent,
    RequiresCapabilityDirective

  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './linkedin-main.component.html',
  styleUrl: './linkedin-main.component.scss'
})
export class LinkedinMainComponent implements OnInit, OnDestroy {

  currentCompany: SelectableCompany | null = null;
  sequences: OutreachSequence[] = [];
  templates: LinkedInTemplate[] = [];
  activeTabIndex: number = 0; // 0 for Sequences, 1 for Templates
  tabs: MenuItem[];
  activeTab: MenuItem;
  showSequences = true;
  showTemplates = false;
  showNewSequence = false;
  showNewTemplate = false;
  showImportLeads = false;
  showLeads = false;
  activeView = 'sequences';
  sequencesLoading = false;
  templatesLoading = false;
  linkedInTemplates: Template[] = [];
  integrations: Integration[] = [];
  selectedIntegration: Integration | null = null;
  private destroy$ = new Subject<void>();
  showImportDialog = false;
  prospectLayout: 'grid' | 'list' = 'grid';
  prospects: LeadsList[] = [];
  availableProspects: LeadsList[] = [];
  hooks: any = [];
  editingTemplate: LinkedInTemplate | null = null;
  isCopyTemplate = false;
  private currentLoadingCompanyId: string | null = null;
  isLoading = false;
  dataLoaded = false;
  private tabsDataLoaded = {
    sequences: false,
    templates: false,
    prospects: false
  };
  
  // Pagination support
  sequencesPagination = { page: 1, limit: 50, hasMore: true };
  templatesPagination = { page: 1, limit: 50, hasMore: true };
  
  // Virtual scrolling support
  virtualScrollOptions = {
    itemHeight: 60, // Height of each row in pixels
    numToleratedItems: 5 // Number of items to render outside visible area
  };
  constructor(
    private integrationsService: IntegrationsService,
    private prospectService: ProspectService,
    private router: Router,
    private linkedInService: LinkedInService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthService,
    private outreachService: OutreachService,
    private companiesService: CompaniesService
  ) {
    this.tabs = [
      { label: 'Sequences', icon: 'pi pi-list' },
      // { label: 'Templates', icon: 'pi pi-file' },
      // { label: 'Prospects', icon: 'pi pi-users' }
    ];

    this.activeTab = this.tabs[0];
  }

  ngOnInit() {
    this.setupCompanySubscription();
    this.setupRouterSubscription();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupCompanySubscription(): void {
    // Subscribe to company changes with debouncing to prevent rapid successive calls
    this.companiesService.selectedCompany$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev?.companyId === curr?.companyId),
        debounceTime(300), // Wait 300ms before processing company changes
        tap(company => console.log('Company emission (debounced):', company))
      )
      .subscribe(company => {
        if (company && company.companyId) {
          // Only load if this is a different company
          if (company.companyId !== this.currentCompany?.companyId) {
            console.log('Company changed from', this.currentCompany?.companyId, 'to', company.companyId);
            this.currentCompany = company;
            this.loadDataWithOptimizations();
            
          }
        } else {
          console.log('No valid company received:', company);
          this.currentCompany = null;
          // Clear data when no company is selected
          this.sequences = [];
          this.templates = [];
          this.prospects = [];
          this.integrations = [];
        }
      });
  }

  private setupRouterSubscription(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateCurrentRoute();
    });
  }

  private updateCurrentRoute() {
    const url = this.router.url;

  }

  // Optimized data loading - only load sequences (first tab) immediately
  loadDataWithOptimizations() {
    // Don't load data if no company is selected
    if (!this.currentCompany?.companyId) {
      console.log('No company selected, skipping data load');
      return;
    }

    const companyId = this.currentCompany.companyId;

    // Prevent duplicate calls for the same company
    if (this.currentLoadingCompanyId === companyId) {
      console.log('Already loading data for this company:', companyId);
      return;
    }

    // Prevent any loading if already in progress
    if (this.isLoading) {
      console.log('Already loading, skipping duplicate call');
      return;
    }

    this.currentLoadingCompanyId = companyId;
    this.isLoading = true;
    this.dataLoaded = false;
    
    // Reset tab loading states
    this.tabsDataLoaded = {
      sequences: false,
      templates: false,
      prospects: false
    };

    console.log(`Loading LinkedIn data with optimizations for company: ${companyId} (${this.currentCompany.companyName})`);

    // Only load sequences (first tab) immediately - other tabs will load on demand
    this.loadSequencesData(companyId, 1, true);
    
    // Load integrations (always needed for webhooks)
    this.loadIntegrationsData(companyId);
  }


  private getCurrentTabName(): string {
    if (this.showSequences) return 'sequences';
    if (this.showTemplates) return 'templates';
    if (this.showLeads) return 'prospects';
    return 'sequences';
  }

  private loadSequencesData(companyId: string, page = 1, forceRefresh = false) {
    if (!forceRefresh && this.tabsDataLoaded.sequences && this.currentCompany?.companyId === companyId && page === 1) {
      console.log('Sequences already loaded for this company');
      return;
    }

    this.sequencesLoading = true;
    
    this.linkedInService.getLinkedInSequences(companyId, page, this.sequencesPagination.limit)
      .then(result => {
        if (this.currentCompany?.companyId === companyId) {
          const sequences = (result as any)?.data?.sequences || result || [];
          
          if (page === 1) {
            this.sequences = sequences;
          } else {
            this.sequences = [...this.sequences, ...sequences];
          }
          
          // Update pagination state
          this.sequencesPagination.page = page;
          this.sequencesPagination.hasMore = sequences.length === this.sequencesPagination.limit;
          
          this.tabsDataLoaded.sequences = true;
          console.log(`✅ Sequences loaded (page ${page})`);
        }
      })
      .catch(error => {
        console.error('Error loading sequences:', error);
        if (page === 1) {
          this.sequences = [];
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.sequencesLoading = false;
        this.checkAllDataLoaded();
      });
  }

  private loadTemplatesData(companyId: string, page = 1, forceRefresh = false) {
    if (!forceRefresh && this.tabsDataLoaded.templates && this.currentCompany?.companyId === companyId && page === 1) {
      console.log('Templates already loaded for this company');
      return;
    }

    this.templatesLoading = true;
    
    this.linkedInService.getLinkedInTemplates(companyId, page, this.templatesPagination.limit)
      .then(result => {
        if (this.currentCompany?.companyId === companyId) {
          const templates = (result as any)?.data?.templates || result || [];
          
          if (page === 1) {
            this.processTemplates(templates);
          } else {
            // Append new templates
            this.processTemplatesAppend(templates);
          }
          
          // Update pagination state
          this.templatesPagination.page = page;
          this.templatesPagination.hasMore = templates.length === this.templatesPagination.limit;
          
          this.tabsDataLoaded.templates = true;
          console.log(`✅ Templates loaded (page ${page})`);
        }
      })
      .catch(error => {
        console.error('Error loading templates:', error);
        if (page === 1) {
          this.templates = [];
          this.linkedInTemplates = [];
        }
      })
      .finally(() => {
        this.templatesLoading = false;
        this.checkAllDataLoaded();
      });
  }

  private loadProspectsData(companyId: string, forceRefresh = false) {
    if (!forceRefresh && this.tabsDataLoaded.prospects && this.currentCompany?.companyId === companyId) {
      console.log('Prospects already loaded for this company');
      return;
    }

    this.prospectService.getProspectLists(companyId, 'linkedin')
      .then(result => {
        if (this.currentCompany?.companyId === companyId) {
          if (result?.data?.lists) {
            this.prospects = result.data.lists;
            this.availableProspects = result.data.lists.filter((e: any) => e.providerListId == null);
          } else {
            console.warn('No prospects data or unexpected structure:', result);
            this.prospects = [];
          }
          this.tabsDataLoaded.prospects = true;
          console.log('✅ Prospects loaded');
        }
      })
      .catch(error => {
        console.error('Error loading prospects:', error);
        this.prospects = [];
      })
      .finally(() => {
        this.checkAllDataLoaded();
      });
  }

  private loadIntegrationsData(companyId: string) {
    this.integrationsService.getIntegrations(this.authService.userId(), companyId)
      .then(result => {
        if (this.currentCompany?.companyId === companyId) {
          this.integrations = (result as any)?.data || result || [];
          this.getWebooks();
          console.log('✅ Integrations loaded');
        }
      })
      .catch(error => {
        console.error('Error loading integrations:', error);
        this.integrations = [];
      })
      .finally(() => {
        this.checkAllDataLoaded();
      });
  }

  private checkAllDataLoaded() {
    // Only check for sequences (first tab) and integrations for initial load completion
    const sequencesLoaded = this.tabsDataLoaded.sequences;
    const integrationsLoaded = this.integrations.length > 0 || this.currentLoadingCompanyId === null;
    
    if (sequencesLoaded && integrationsLoaded) {
      this.isLoading = false;
      this.dataLoaded = true;
      this.currentLoadingCompanyId = null;
      console.log('🚀 Essential LinkedIn data loaded successfully (sequences + integrations)');
    }
  }

  loadData() {
    // Don't load data if no company is selected
    if (!this.currentCompany?.companyId) {
      console.log('No company selected, skipping data load');
      return;
    }

    const companyId = this.currentCompany.companyId;

    // Prevent duplicate calls for the same company
    if (this.currentLoadingCompanyId === companyId) {
      console.log('Already loading data for this company:', companyId);
      return;
    }

    // Prevent any loading if already in progress
    if (this.isLoading) {
      console.log('Already loading, skipping duplicate call');
      return;
    }

    this.currentLoadingCompanyId = companyId;
    this.isLoading = true;
    this.dataLoaded = false;
    this.sequencesLoading = true;
    this.templatesLoading = true;

    console.log(`Loading LinkedIn data for company: ${companyId} (${this.currentCompany.companyName})`);

    forkJoin({
      sequences: this.linkedInService.getLinkedInSequences(companyId),
      templates: this.linkedInService.getLinkedInTemplates(companyId),
      prospects: this.prospectService.getProspectLists(companyId, 'linkedin'),
      integrations: this.integrationsService.getIntegrations(this.authService.userId(), companyId),
    }).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading LinkedIn data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load LinkedIn data'
        });
        return of({ sequences: [], templates: [], prospects: { data: { lists: [] } }, integrations: { data: [] } });
      }),
      finalize(() => {
        this.sequencesLoading = false;
        this.templatesLoading = false;
        this.isLoading = false;
        this.dataLoaded = true;
        this.currentLoadingCompanyId = null;
      })
    ).subscribe({
      next: async (result) => {
        // Only process results if we're still on the same company
        if (this.currentCompany?.companyId === companyId) {
          console.log('Data loaded successfully for company:', companyId);

          this.sequences = (result.sequences as any)?.data?.sequences || result.sequences || [];
          this.processTemplates((result.templates as any)?.data?.templates || result.templates || []);
          this.integrations = (result.integrations as any)?.data || result.integrations || [];

          // Fix the prospects error with better error handling
          if (result.prospects?.data?.lists) {
            this.prospects = result.prospects.data.lists;
            this.availableProspects = result.prospects.data.lists.filter((e: any) => e.providerListId == null);
          } else {
            console.warn('No prospects data or unexpected structure:', result.prospects);
            this.prospects = [];
          }

          await this.getWebooks();
        } else {
          console.log('Company changed during load, discarding results for:', companyId);
        }
      }
    });
  }

  // Add method to manually refresh data
  refreshData(): void {
    if (this.currentCompany) {
      console.log('🔄 Manual refresh triggered');
      
      // Clear cache for this company
      this.linkedInService.clearCompanyCache(this.currentCompany.companyId);
      this.prospectService.clearCompanyCache(this.currentCompany.companyId);
      
      // Reset tab loading states to force reload
      this.tabsDataLoaded = {
        sequences: false,
        templates: false,
        prospects: false
      };
      
      // Reset pagination states
      this.sequencesPagination = { page: 1, limit: 50, hasMore: true };
      this.templatesPagination = { page: 1, limit: 50, hasMore: true };
      
      // Force refresh the current tab data
      const currentTab = this.getCurrentTabName();
      this.lazyLoadTabData(currentTab as 'sequences' | 'templates' | 'prospects', true);
      
      // Also refresh integrations
      this.loadIntegrationsData(this.currentCompany.companyId);
      
      this.messageService.add({
        severity: 'success',
        summary: 'Data Refreshed',
        detail: 'LinkedIn data has been updated',
        life: 3000
      });
    }
  }

  // Helper methods for template
  getCurrentCompanyDisplayName(): string {
    if (this.currentCompany) {
      return this.companiesService.getCompanyDisplayName(this.currentCompany);
    }
    return 'No company selected';
  }


  isViewingParent(): boolean {
    return this.companiesService.isCurrentSelectionParent();
  }

  canManageUsers(): boolean {
    return this.companiesService.hasPermission('manage_users');
  }

  canWrite(): boolean {
    return this.companiesService.hasPermission('write');
  }

  getLoadingMessage(): string {
    if (this.currentCompany) {
      return `Loading LinkedIn data for ${this.currentCompany.companyName}...`;
    }
    return 'Loading LinkedIn data...';
  }

  async getWebooks() {
    if (!this.currentCompany) return;

    const id = await this.currentCompany.companyId;
    this.hooks = await this.linkedInService.getLinkedinWebhooks(id);
  }

  hasWebHook(sequnce: EmailSequence) {
    const hook = this.hooks.find((hook: any) => hook.campaignId === sequnce.providerCampaignId)
    return !hook;
  }

  showView(view: 'sequences' | 'templates' | 'prospects' | 'new-sequence' | 'new-template' | 'import-leads') {
    switch (view) {
      case 'sequences':
        this.activeView = 'sequences';
        this.showSequences = true;
        this.showTemplates = false;
        this.showNewSequence = false;
        this.showNewTemplate = false;
        this.showLeads = false;
        this.showImportLeads = false;
        this.lazyLoadTabData('sequences');
        break;
      case 'templates':
        this.activeView = 'templates';
        this.showSequences = false;
        this.showTemplates = true;
        this.showNewSequence = false;
        this.showNewTemplate = false;
        this.showLeads = false;
        this.showImportLeads = false;
        this.lazyLoadTabData('templates');
        break;
      case 'prospects':
        this.activeView = 'leads';
        this.showSequences = false;
        this.showTemplates = false;
        this.showLeads = true
        this.showNewSequence = false;
        this.showNewTemplate = false;
        this.showImportLeads = false;
        this.lazyLoadTabData('prospects');
        break;
      case 'new-sequence':
        this.activeView = 'new-sequence';
        this.showSequences = false;
        this.showTemplates = false;
        this.showNewSequence = true;
        this.showNewTemplate = false;
        this.showLeads = false;
        this.showImportLeads = false;
        break;
      case 'new-template':
        this.activeView = 'new-template';
        this.showSequences = false;
        this.showTemplates = false;
        this.showNewSequence = false;
        this.showNewTemplate = true;
        this.showLeads = false;
        this.showImportLeads = false;
        break;
      case 'import-leads':
        this.activeView = 'import-leads';
        this.showSequences = false;
        this.showTemplates = false;
        this.showLeads = false;
        this.showNewSequence = false;
        this.showNewTemplate = false;
        this.showImportLeads = true;
        break;
    }
  }

  private lazyLoadTabData(tabName: 'sequences' | 'templates' | 'prospects', forceRefresh = false) {
    if (!this.currentCompany?.companyId) return;
    
    const companyId = this.currentCompany.companyId;
    
    // Load data for this tab if not already loaded
    switch (tabName) {
      case 'sequences':
        if (!this.tabsDataLoaded.sequences || forceRefresh) {
          this.loadSequencesData(companyId, 1, forceRefresh);
        }
        break;
      case 'templates':
        if (!this.tabsDataLoaded.templates || forceRefresh) {
          this.loadTemplatesData(companyId, 1, forceRefresh);
        }
        break;
      case 'prospects':
        if (!this.tabsDataLoaded.prospects || forceRefresh) {
          this.loadProspectsData(companyId, forceRefresh);
        }
        break;
    }
  }

  processTemplates(data: any[]): void {
    if (!data || !data.length) return;

    // Process templates in chunks to avoid blocking UI
    this.processTemplatesAsync(data);
  }

  private async processTemplatesAsync(data: any[]): Promise<void> {
    const CHUNK_SIZE = 10;
    const chunks = this.chunkArray(data, CHUNK_SIZE);
    
    this.linkedInTemplates = [];
    this.templates = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      // Process chunk
      const linkedInTemplateChunk = chunk.map((template: any) => ({
        label: template.name,
        value: template.id || 0
      }));

      const templateChunk = chunk.map((temp: any) => ({
        id: temp.id,
        name: temp.name,
        label: templateTypes.find(e => e.value == temp.type)?.label + '',
        type: temp.type,
        content: temp.body,
        createdDate: temp.createdAt,
        updatedAt: temp.updatedAt
      }));

      // Add to arrays
      this.linkedInTemplates.push(...linkedInTemplateChunk);
      this.templates.push(...templateChunk);

      // Yield control to browser between chunks
      if (i < chunks.length - 1) {
        await this.delay(0);
      }
    }

    console.log(`✅ Processed ${data.length} templates in ${chunks.length} chunks`);
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private processTemplatesAppend(data: any[]): void {
    if (!data || !data.length) return;

    // Append templates efficiently
    const linkedInTemplateChunk = data.map((template: any) => ({
      label: template.name,
      value: template.id || 0
    }));

    const templateChunk = data.map((temp: any) => ({
      id: temp.id,
      name: temp.name,
      label: templateTypes.find(e => e.value == temp.type)?.label + '',
      type: temp.type,
      content: temp.body,
      createdDate: temp.createdAt,
      updatedAt: temp.updatedAt
    }));

    this.linkedInTemplates.push(...linkedInTemplateChunk);
    this.templates.push(...templateChunk);
    
    console.log(`✅ Appended ${data.length} templates`);
  }

  // Pagination methods
  loadMoreSequences(): void {
    if (!this.currentCompany || !this.sequencesPagination.hasMore || this.sequencesLoading) {
      return;
    }

    const nextPage = this.sequencesPagination.page + 1;
    this.loadSequencesData(this.currentCompany.companyId, nextPage);
  }

  loadMoreTemplates(): void {
    if (!this.currentCompany || !this.templatesPagination.hasMore || this.templatesLoading) {
      return;
    }

    const nextPage = this.templatesPagination.page + 1;
    this.loadTemplatesData(this.currentCompany.companyId, nextPage);
  }

  // Virtual scrolling event handlers
  onScrollIndexChange(event: any, type: 'sequences' | 'templates'): void {
    // Load more data when user scrolls near the end
    const { first, last } = event;
    const dataLength = type === 'sequences' ? this.sequences.length : this.templates.length;
    const pagination = type === 'sequences' ? this.sequencesPagination : this.templatesPagination;
    
    // Load more when scrolling to last 10 items
    if (last >= dataLength - 10 && pagination.hasMore) {
      if (type === 'sequences') {
        this.loadMoreSequences();
      } else {
        this.loadMoreTemplates();
      }
    }
  }

  // Utility methods for virtual scrolling
  trackBySequenceId(index: number, sequence: any): any {
    return sequence.sequenceId || sequence.id || index;
  }

  trackByTemplateId(index: number, template: any): any {
    return template.id || index;
  }

  async startSequence(sequence: EmailSequence) {
    try {
      await this.outreachService.startSequence(sequence.providerCampaignId, sequence.provider, sequence.sequenceId, this.currentCompany!.companyId);
      this.messageService.add({
        severity: 'success',
        summary: 'Sequence Started',
        detail: `${sequence.name} has been started on ${sequence.provider}`
      });
      this.refreshData();
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to start sequence'
      });
    }
  }

  async pauseSequence(sequence: EmailSequence) {
    try {
      await this.outreachService.pauseSequence(sequence.providerCampaignId, sequence.provider, sequence.sequenceId, this.currentCompany!.companyId);
      this.messageService.add({
        severity: 'success',
        summary: 'Sequence Paused',
        detail: `${sequence.name} has been paused on ${sequence.provider}`
      });
      this.refreshData();
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to pause sequence'
      });
    }
  }

  async deleteSequence(sequence: EmailSequence) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the sequence "${sequence.name || 'this sequence'}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        try {
          await this.outreachService.deleteEmailSequence(sequence, this.currentCompany!.companyId);
          this.messageService.add({
            severity: 'success',
            summary: 'Sequence Deleted',
            detail: `${sequence.name} has been deleted`
          });
          this.refreshData();
        } catch (e) {
          this.messageService.add({
            severity: 'error',
            summary: 'Failure to Delete Sequence',
            detail: `${sequence.name} could not be deleted`
          });
        }
      },
      reject: () => {
        // Optional: Handle rejection (user clicked cancel)
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelled',
          detail: 'Delete operation cancelled'
        });
      }
    });
  }

  getSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warn';
      case 'draft':
        return 'info';
      case 'completed':
        return 'secondary';
      case 'deleted':
        return 'danger';
      default:
        return 'info';
    }
  }

  newSequence() {
    this.showView('new-sequence');
  }

  newTemplate() {
    this.showView('new-template');
  }

  editTemplate(template: any) {
    this.editingTemplate = template;
    this.showView('new-template');
  }

  async deleteTemplate(template: any) {
    try {
      await this.outreachService.deleteTemplate(template.id, this.currentCompany!.companyId);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Template deleted successfully'
      });
      this.refreshData(); // Refresh after deletion
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete template'
      });
    }
  }

  copyTemplate(template: any) {
    this.isCopyTemplate = true;
    this.editingTemplate = JSON.parse(JSON.stringify(template));
    this.editingTemplate!.id = undefined;
    this.showView('new-template');
  }

  async createWebhook(sequence: EmailSequence) {
    try {
      await this.linkedInService.setupLinkedinWebhook(sequence, this.currentCompany!.companyId);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Webhook created successfully'
      });
      await this.getWebooks(); // Refresh webhooks
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create webhook'
      });
    }
  }

  syncLeadList(sequence: EmailSequence) {
    // Implementation for syncing lead list
  }

  onTabChange(event: any) {
    this.showView(event.label.toLowerCase());
  }

  completeNewSequence = async (data: LinkedInSequence): Promise<void> => {
    if (data.selectedLeadsList != undefined && data.selectedLeadsList?.prospectListId == data.selectedExcludeList?.prospectListId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'The Leads list and the Excluded Leads List cannot be the same'
      });
      return;
    }

    try {
      data.steps.forEach(step => {
        const temp = this.templates.find(temp => temp.id === step.template?.value);
        step.templateName = temp?.name;
        step.templateBody = temp?.content;
        step.templateId = temp?.id;
        step.waitUnit = (step.waitUnit as any)?.value || step.waitUnit;
      });

      let platRep!: NewSequenceResponse;
      if (data.action == 'update') {
        // Handle update logic
      } else {
        const rep = await this.linkedInService.createLinkedInSequence(data, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Sequence Saved',
          detail: 'Now adding your sequence to your integration partners'
        });
      }
      this.refreshData();

    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save sequence and add it to your integration partner'
      });
      console.error('Error saving sequence:', error);
    }
    this.showView('sequences');
  }

  async addLeadsToCampaign(sequence: LinkedInSequence) {
    await this.prospectService.createProspectListInPlatforms(sequence?.prospectListId, 'linkedin', this.currentCompany!.companyId);
    this.messageService.add({
      severity: 'success',
      summary: 'Addings Leads to Heyreach',
      detail: `The propspects have been sent to Instantly... Please wait`
    });
  }

  cancelNewSequence = (): void => {
    this.editingTemplate = null;
    this.showView('sequences');
  }

  getTooltipContent(prospect: any): string {
    if (prospect.linkedInSenderEmail || prospect.linkedInSenderName) {
      return `
        <div style="text-align: left;">
          <div ><strong>LinkedIn Sender Details:</strong></div>
          <div style="margin-bottom: 2px;"><strong>Name:</strong> ${prospect.linkedInSenderName || 'N/A'}</div>
          <div><strong>Email:</strong> ${prospect.linkedInSenderEmail || 'N/A'}</div>
        </div>
      `;
    }
    return 'No LinkedIn sender details available';
  }

  completeNewTemplate = async (data: any): Promise<void> => {
    try {
      let result;

      if (data.action == 'update') {
        result = await this.linkedInService.updateLinkedInTemplate(data, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template updated successfully'
        });
      } else {
        result = await this.linkedInService.createLinkedInTemplate(data, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template created successfully'
        });
      }
      this.refreshData();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save template'
      });
      console.error('Error saving template:', error);
    }
    this.editingTemplate = null;
    this.showView('templates');
  }

  cancelNewTemplate = (): void => {
    this.editingTemplate = null;
    this.showView('templates');
  }

  importLeads() {
    this.showImportDialog = true;
  }



  navigateToMetrics(sequence: any): void {
    this.router.navigate(['/linkedin/metrics', sequence.sequenceId, sequence.companyId], { queryParamsHandling: 'merge' });
  }

  navigateToDetails(sequence: any): void {
    this.router.navigate(['/linkedin/details', sequence.sequenceId, sequence.companyId], { queryParamsHandling: 'merge' });
  }

  onImportCancelled() { }

  async onProspectsImported(event: { file: File, mappings: { [key: string]: string }, listName: string, linkedInAccountId?: number, linkedInSenderEmail?: string,linkedInSenderName?:string, sequenceId?: string }): Promise<void> {
    await this.processProspectImport(event.file, event.mappings, event.listName, event.linkedInAccountId, event.linkedInSenderEmail, event.linkedInSenderName, event.sequenceId);
    this.messageService.add({
      severity: 'success',
      summary: 'Import Started',
      detail: `Importing ${event.file.name} with ${Object.keys(event.mappings).length} mapped fields`
    });
  }

  private async processProspectImport(file: File, mappings: { [key: string]: string }, listName: string, linkedInAccountId?: number, linkedInSenderEmail?: string, linkedInSenderName?: string, sequenceId?: string): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    formData.append('listName', listName);
    if (linkedInAccountId) {
      formData.append('linkedInAccountId', linkedInAccountId?.toString() || '');
    }
    if (linkedInSenderEmail) {
      formData.append('linkedInSenderEmail', linkedInSenderEmail || '');
    }
    if (linkedInSenderName) {
      formData.append('linkedInSenderName', linkedInSenderName || '');
    }
    if (sequenceId) {
      formData.append('sequenceId', sequenceId || '');
    }

    try {
      const rep = await this.prospectService.importProspects(formData, 'linkedin', this.currentCompany!.companyId);
      this.messageService.add({
        severity: 'success',
        summary: 'Import Success',
        detail: `All prospects have been imported into Anchored Growth.`
      });
      this.refreshData(); // Refresh data after import
    } catch (error) {
      console.error('Error processing import:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Import Failure',
        detail: `An error occurred importing prospects`
      });
    }
  }
}
